/* v6.35.9 — Tiny health-dot for the SuperAdmin sidebar.
 *
 * Polls GET /api/bp-auditor/diagnostics/recent-wedges?days=7 every 60s.
 * Renders a single colored dot next to the "Content Auditor" sidebar entry:
 *   - green  : 0 wedges (production healthy)
 *   - amber  : 1-5 wedges (watch list)
 *   - red    : >5 wedges (systemic issue, investigate)
 *
 * Non-blocking, silent on errors. Tooltip surfaces the exact count + days
 * window so the operator gets context without opening the panel.
 *
 * v6.35.10 — Tone-degradation notification.
 *   When the tone transitions FROM a healthier bucket TO a less-healthy
 *   one (green→amber, green→red, amber→red), fire a desktop notification
 *   so the operator gets pinged the instant production health degrades
 *   even when they're not looking at the screen. Improvements (red→green,
 *   amber→green, red→amber) are silent — no need to interrupt for good
 *   news. Initial-load tone is established WITHOUT firing a notification
 *   to avoid spamming on every page refresh.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { notifyOnCompletion } from '../utils/jobCompletionNotifier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const tone = (n) => {
  if (n === 0) return { color: 'bg-emerald-500', label: 'healthy' };
  if (n <= 5) return { color: 'bg-amber-500', label: 'watch' };
  return { color: 'bg-rose-600', label: 'investigate' };
};

// v6.35.10 — ordering for degradation comparison. Higher index = worse.
const TONE_RANK = { healthy: 0, watch: 1, investigate: 2 };

const WedgeHealthDot = () => {
  const [total, setTotal] = useState(null);
  // v6.35.10 — remember the prior tone label across polls so we can
  // detect degradation transitions. Use a ref so updates don't trigger
  // a re-render (the dot already re-renders on `total` state).
  const prevToneRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/bp-auditor/diagnostics/recent-wedges?days=7`,
        authCfg(),
      );
      const next = typeof data?.total_wedges === 'number' ? data.total_wedges : null;
      setTotal(next);

      // v6.35.10 — tone-degradation notification. Only fire on transitions
      // toward a WORSE bucket; never on first load (prev === null) and
      // never on recovery.
      if (next !== null) {
        const nextTone = tone(next).label;
        const prev = prevToneRef.current;
        if (prev !== null && TONE_RANK[nextTone] > TONE_RANK[prev]) {
          notifyOnCompletion({
            title: `Production health degraded: ${prev} → ${nextTone}`,
            body: `${next} wedge${next === 1 ? '' : 's'} in last 7 days — open Content Auditor → Recent Wedges`,
            success: false,
          });
        }
        prevToneRef.current = nextTone;
      }
    } catch {
      // Silent — non-critical companion indicator.
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (total === null) return null;
  const { color, label } = tone(total);

  return (
    <span
      className="inline-flex items-center"
      title={`${total} wedge${total === 1 ? '' : 's'} in last 7 days — ${label}`}
      data-testid="sa-wedge-health-dot"
      data-wedge-count={total}
      data-wedge-tone={label}
    >
      <span
        className={`inline-block w-2 h-2 rounded-full ${color}`}
        aria-label={`Wedge health: ${label} (${total} in 7 days)`}
      />
    </span>
  );
};

export default WedgeHealthDot;
